<script setup lang="ts">
	import tripadvisorLogo from '@/assets/imgs/tripadvisor-logo.png'
	import axios from '@/lib/axios'
	import { ChevronDownIcon } from '@heroicons/vue/20/solid'
	import { onBeforeUnmount, ref, watch } from 'vue'

	import LoadingSpin from '@/components/icons/LoadingSpin.vue'

	import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

	import type { TripadvisorDetails as Details } from '../interfaces/tripadvisorDetails'
	import TripadvisorDetails from './TripadvisorDetails.vue'

	const props = defineProps<{ hotelId: number }>()
	const root = ref<HTMLElement | null>(null)
	const expanded = ref(false)
	const loading = ref(false)
	const loaded = ref(false)
	const details = ref<Partial<Details> | null>(null)
	const message = ref('')
	const error = ref('')
	let request: AbortController | undefined

	const reset = () => {
		request?.abort()
		expanded.value = false
		loading.value = false
		loaded.value = false
		details.value = null
		message.value = ''
		error.value = ''
	}

	watch(() => props.hotelId, reset)
	onBeforeUnmount(() => request?.abort())

	const load = async () => {
		if (loading.value || loaded.value) return
		const controller = new AbortController()
		request = controller
		loading.value = true
		error.value = ''

		try {
			const { data } = await axios.get<{
				status: 'found' | 'not_found' | 'unavailable'
				details: Partial<Details> | null
				message: string | null
			}>(`/api/hotels/${props.hotelId}/tripadvisor`, {
				signal: controller.signal,
				timeout: 25000,
				errorHandling: false,
			} as CustomAxiosRequestConfig)
			if (controller.signal.aborted) return
			details.value = data.details
			message.value =
				data.message ??
				'No hay información de Tripadvisor disponible para este hotel.'
			loaded.value = true
		} catch {
			if (controller.signal.aborted) return
			error.value =
				'No fue posible cargar Tripadvisor. Puedes intentar nuevamente más tarde.'
		} finally {
			if (!controller.signal.aborted) loading.value = false
		}
	}

	const toggle = () => {
		expanded.value = !expanded.value
		if (expanded.value && !loaded.value && !error.value) void load()
	}

	// StyledButton's blue hover and bordered look read as a select inside this panel.
	const pillButton =
		'inline-flex min-h-[44px] max-w-full shrink-0 items-center justify-center gap-2 rounded-full bg-slate-900 px-5 text-sm font-semibold text-white transition-colors hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 disabled:cursor-wait motion-reduce:transition-none'

	// The Tripadvisor tab scrolls here; opening stays up to the user.
	defineExpose({ root })
</script>

<template>
	<section
		ref="root"
		class="w-full min-w-0 rounded-lg border border-emerald-200 border-t-[3px] border-t-[#34e0a1] bg-emerald-50/60 p-4 shadow-sm md:rounded-xl md:p-5"
		aria-label="Tripadvisor"
	>
		<div
			class="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
		>
			<div class="min-w-0 space-y-3">
				<div class="flex flex-wrap items-center gap-x-3 gap-y-2">
					<h2 class="text-sm font-semibold text-slate-700">
						Información adicional
					</h2>
					<span
						class="rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-medium text-emerald-800"
					>
						Fuente externa
					</span>
				</div>
				<img
					:src="tripadvisorLogo"
					alt="Tripadvisor"
					width="489"
					height="105"
					class="h-auto w-[168px] max-w-full sm:w-[186px]"
				/>
				<p class="text-sm leading-relaxed text-slate-600">
					Calificaciones y opiniones de viajeros publicadas en Tripadvisor.
				</p>
			</div>
			<button
				type="button"
				:class="[pillButton, 'self-stretch sm:self-auto']"
				:disabled="loading"
				:aria-expanded="expanded"
				:aria-controls="`tripadvisor-${hotelId}`"
				@click="toggle"
			>
				<span>{{ expanded ? 'Contraer detalles' : 'Expandir detalles' }}</span>
				<LoadingSpin
					v-if="loading"
					spin-class="h-4 w-4"
				/>
				<ChevronDownIcon
					v-else
					class="h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none"
					:class="{ 'rotate-180': expanded }"
					aria-hidden="true"
				/>
			</button>
		</div>
		<div
			v-show="expanded"
			:id="`tripadvisor-${hotelId}`"
			class="mt-5 min-w-0 break-words border-t border-emerald-200 pt-5"
			:aria-busy="loading"
		>
			<p
				v-if="loading"
				role="status"
			>
				Cargando información de Tripadvisor…
			</p>
			<div
				v-else-if="error"
				class="flex flex-col items-start gap-3"
			>
				<p role="alert">{{ error }}</p>
				<button
					type="button"
					:class="pillButton"
					@click="load"
				>
					Reintentar
				</button>
			</div>
			<TripadvisorDetails
				v-else-if="details"
				:details="details"
			/>
			<p
				v-else-if="loaded"
				role="status"
			>
				{{ message }}
			</p>
		</div>
	</section>
</template>
